/**
 * video_controller.js — start and keep the hero demo clip playing.
 *
 * Element-scoped controller (data-controller="video") on the landing hero's .phone
 * wrapper, with the app demo clip as its `media` target. The <video> carries no
 * `autoplay` attribute and preload="none" (see phone_video.html), so this
 * controller is what starts it. On a fresh document the first play() waits for the
 * window `load` event: the clip is large, and fetching it before load puts it in
 * contention with the page's own critical assets. Turbo Drive swaps the <body> on
 * every in-app navigation (and again when it restores a cached snapshot on
 * Back/Forward); connect() runs on each insertion, and by then the document has
 * already loaded, so playback starts at once.
 *
 * Why the retry: on a cold body-swap the freshly-inserted element has not buffered
 * yet (readyState 0/1). Calling play() that early returns a promise that the pending
 * media load rejects (AbortError), leaving the clip paused — the intermittent
 * "video didn't come back" race. When the snapshot is warm in Turbo's cache the same
 * call succeeds, which is why it was non-deterministic. So play() is attempted
 * immediately (fast path for the warm case) AND again on `canplay`/`loadeddata`, the
 * events the element fires once it actually has data. The video is muted + playsinline,
 * which
 * satisfies the autoplay policy; play()'s promise can still reject, so it is
 * swallowed rather than left unhandled. The bound listener is removed in disconnect()
 * so it never fires against a detached element after the next navigation.
 *
 * Frame sync (opt-in): the hero clip opens with live footage and its own in-video
 * phone bezel, so the page's static bezel (.phone .frame-img) only shows from
 * data-video-frame-show-at (seconds) — through to the end unless an optional
 * data-video-frame-hide-at says otherwise. A requestAnimationFrame loop — not
 * `timeupdate`, which only fires every ~250ms and would visibly lag the hard cut —
 * toggles .frameless on the controller element. The loop survives the clip's
 * `loop` wrap-around since it reads currentTime fresh every tick.
 *
 * The bezel outlives the screen recording: the clip's sign-off card is full-bleed
 * and wider than the recording, so it has to be shown whole rather than cropped to
 * the recording's window. data-video-screen-until marks where the recording stops;
 * past it .uncropped joins .frameless-off so the bezel stays but the crop lifts
 * (see landing.css .phone .stage). Omit it and the crop simply runs to the end.
 *
 * Sound: autoplay must start muted (browser policy blocks unmuted autoplay), so the
 * clip's music is opt-in — toggleMute() runs off a click anywhere on the video or
 * on the soundHint pill, a user gesture the policy accepts. connect()'s muted
 * re-assert also resets sound after a Turbo navigation, which keeps autoplay legal
 * there. That same click raises the transport bar, which is how touch reaches it;
 * making the click do ONLY that was a regression — on a pointer device hover has
 * already raised the bar, so clicking the video appeared to do nothing at all.
 *
 * Transport (opt-in, needs the seek/playToggle/time targets): a play-pause,
 * back-10s and scrub bar, raised on hover, on focus, while paused, and — for touch
 * — by revealControls() for a few seconds after a tap. The native `controls` attribute cannot be
 * used — it paints inside the <video> box, which landing.css scales to 137% and
 * clip-paths into the bezel cutout, so the bar would land oversized, offset and
 * clipped out of sight. Readouts ride the frame-sync rAF loop rather than adding a
 * `timeupdate` listener, so there is still exactly one clock driving the element.
 *
 * _userPaused is why pausing sticks: seeking makes the element re-fire `canplay`,
 * and the autoplay-recovery listener above answers that by calling play(). Without
 * the flag, scrubbing a paused clip would silently restart it.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        static targets = ['media', 'soundHint', 'playToggle', 'seek', 'time'];

        // The wording the controller swaps as the clip changes state. The
        // template (phone_video.html) always sets all four, in the page's language.
        static values = {
            playLabel: String, pauseLabel: String, soundLabel: String, muteLabel: String,
        };

        connect() {
            // A Turbo snapshot restore clones the <video>; the cloned node can lose the
            // IDL `muted` property, which would make the autoplay policy reject play().
            // Re-assert it so muted autoplay is always permitted.
            this.mediaTarget.muted = true;
            this._onReady = this.play.bind(this);
            this.mediaTarget.addEventListener('canplay', this._onReady);
            this.mediaTarget.addEventListener('loadeddata', this._onReady);
            if (document.readyState === 'complete') {
                this.play();
            } else {
                window.addEventListener('load', this._onReady, { once: true });
            }
            this.updateSoundHint();

            this._userPaused = false;
            this._scrubbing = false;
            this._durationSet = false;
            this._wasPaused = null;
            this._lastPct = -1;
            this._lastLabel = null;

            this._frameShowAt = parseFloat(this.element.dataset.videoFrameShowAt);
            this._frameHideAt = parseFloat(this.element.dataset.videoFrameHideAt);
            this._screenUntil = parseFloat(this.element.dataset.videoScreenUntil);
            if (!isNaN(this._frameShowAt) || this.hasSeekTarget) {
                this._onFrameTick = this.syncFrame.bind(this);
                this._frameRaf = requestAnimationFrame(this._onFrameTick);
            }
        }

        disconnect() {
            this.mediaTarget.removeEventListener('canplay', this._onReady);
            this.mediaTarget.removeEventListener('loadeddata', this._onReady);
            window.removeEventListener('load', this._onReady);
            clearTimeout(this._revealTimer);
            if (this._frameRaf) {
                cancelAnimationFrame(this._frameRaf);
                this._frameRaf = null;
            }
        }

        play() {
            if (this._userPaused || !this.mediaTarget.paused) return;
            var playback = this.mediaTarget.play();
            if (playback && typeof playback.catch === 'function') {
                playback.catch(function () {});
            }
        }

        toggleMute() {
            this.mediaTarget.muted = !this.mediaTarget.muted;
            // The toggle is a user gesture, so play() is allowed even where autoplay
            // was blocked — recovers a clip that never started.
            this.play();
            this.updateSoundHint();
            // Clicking the video is also how touch reaches the transport bar, since
            // there is no hover there. Raising it here rather than replacing this
            // handler keeps click-anywhere-for-sound, which is what the video reads
            // as it is doing: it is a video, not a control surface.
            this.revealControls();
        }

        updateSoundHint() {
            if (!this.hasSoundHintTarget) return;
            this.soundHintTarget.textContent =
                this.mediaTarget.muted ? this.soundLabelValue : this.muteLabelValue;
        }

        syncFrame() {
            if (!isNaN(this._frameShowAt)) {
                var t = this.mediaTarget.currentTime;
                var framed = t >= this._frameShowAt &&
                    (isNaN(this._frameHideAt) || t < this._frameHideAt);
                var onScreen = framed &&
                    (isNaN(this._screenUntil) || t < this._screenUntil);
                this.element.classList.toggle('frameless', !framed);
                this.element.classList.toggle('uncropped', framed && !onScreen);
            }
            if (this.hasSeekTarget) this.updateControls();
            this._frameRaf = requestAnimationFrame(this._onFrameTick);
        }

        // Touch has no hover, so a tap on the clip raises the bar instead. Every
        // control re-arms the timer, or the bar would vanish mid-scrub. Harmless on
        // pointer devices, where :hover already has it up.
        revealControls() {
            if (!this.hasSeekTarget) return;
            this.element.classList.add('controls-shown');
            clearTimeout(this._revealTimer);
            var el = this.element;
            this._revealTimer = setTimeout(function () {
                el.classList.remove('controls-shown');
            }, 3500);
        }

        togglePlay() {
            if (this.mediaTarget.paused) {
                this._userPaused = false;
                this.play();
            } else {
                this._userPaused = true;
                this.mediaTarget.pause();
            }
            this.revealControls();
        }

        back10() {
            var v = this.mediaTarget;
            v.currentTime = Math.max(0, v.currentTime - 10);
            this.paint();
            this.revealControls();
        }

        seek() {
            var v = this.mediaTarget;
            if (!isFinite(v.duration)) return;
            v.currentTime = parseFloat(this.seekTarget.value);
            this.paint();
            this.revealControls();
        }

        // While the thumb is held the user owns its position, so the rAF loop must
        // stop writing it back — a playing clip would otherwise drag the thumb
        // forward under the pointer between input events. pointerup is bound to
        // window in the template because releasing outside the slider never fires
        // it on the slider, which would strand the flag on.
        // The mute toggle lives on .stage so the whole screen area is clickable, and
        // the bar sits inside .stage — without this every control would also toggle
        // sound on its way up.
        swallowClick(event) { event.stopPropagation(); }

        scrubStart() { this._scrubbing = true; }

        scrubEnd() { this._scrubbing = false; }

        updateControls() {
            var v = this.mediaTarget;
            if (v.paused !== this._wasPaused) {
                this._wasPaused = v.paused;
                this.element.classList.toggle('is-paused', v.paused);
                this.playToggleTarget.setAttribute(
                    'aria-label', v.paused ? this.playLabelValue : this.pauseLabelValue);
            }
            if (!isFinite(v.duration)) return;
            if (!this._durationSet) {
                this._durationSet = true;
                this.seekTarget.max = v.duration;
            }
            this.paint();
        }

        // Called every frame, so each write is gated on the value actually moving:
        // a range value and a custom property both invalidate style on assignment.
        paint() {
            var v = this.mediaTarget;
            var pct = v.duration ? (v.currentTime / v.duration) * 100 : 0;
            if (Math.abs(pct - this._lastPct) < 0.05) return;
            this._lastPct = pct;
            if (!this._scrubbing) this.seekTarget.value = v.currentTime;
            this.seekTarget.style.setProperty('--vc-progress', pct.toFixed(2) + '%');
            var label = this.formatTime(v.currentTime) + ' / ' +
                this.formatTime(v.duration);
            if (label === this._lastLabel) return;
            this._lastLabel = label;
            this.timeTarget.textContent = label;
        }

        formatTime(secs) {
            if (!isFinite(secs)) return '0:00';
            var s = Math.floor(secs % 60);
            return Math.floor(secs / 60) + ':' + (s < 10 ? '0' : '') + s;
        }
    }
